import { useState, useEffect, useCallback } from 'react';
import type { AuthUser, AuthOrganization } from '../types/auth';
import { AuthVerifyResponseSchema } from '../services/api/schemas';

export const useAuth = () => {
  const [user, setUser] = useState<AuthUser | null>(null);
  // Start with loading true to prevent sign-in page flash
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [organization, setOrganization] = useState<AuthOrganization | null>(null);

  // Always use relative URLs - Caddy proxy handles routing to backend
  const authServiceUrl = '';
  // Default to true unless explicitly disabled
  const isAuthEnabled = import.meta.env.VITE_ENABLE_GOOGLE_AUTH !== 'false';

  const verifyToken = useCallback(async () => {
    try {
      const verifyUrl = '/api/auth/verify';

      const storedToken = localStorage.getItem('auth_token');

      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };

      if (storedToken) {
        headers['Authorization'] = `Bearer ${storedToken}`;
      }

      const response = await fetch(verifyUrl, {
        method: 'POST',
        credentials: 'include',
        headers
      });

      if (response.ok) {
        const raw = await response.json();

        const parseResult = AuthVerifyResponseSchema.safeParse(raw);
        if (!parseResult.success) {
          console.error('useAuth: Auth response validation failed');
          clearAuth();
          return;
        }

        const authData = parseResult.data;
        const userData = authData.user;

        const authUser: AuthUser = {
          id: userData.id,
          email: userData.email,
          full_name: userData.full_name || userData.email,
          picture: userData.picture,
          is_admin: userData.is_admin || false,
          can_access_square: userData.can_access_square || false,
          can_access_ai: userData.can_access_ai || false,
          organization: authData.organization ?? undefined,
          permissions: authData.permissions ?? {}
        };

        setUser(authUser);
        setIsAuthenticated(true);
        setOrganization(authData.organization ?? null);
      } else {
        clearAuth();
      }
    } catch (error) {
      console.error('useAuth: Failed to verify token:', error instanceof Error ? error.message : 'Unknown error');
      clearAuth();
    } finally {
      setIsLoading(false);
    }
  }, []);

  const checkAuthStatus = useCallback(async () => {
    setIsLoading(true);
    await verifyToken();
  }, [verifyToken]);

  useEffect(() => {
    if (!isAuthEnabled) {
      setIsLoading(false);
      setIsAuthenticated(false);
      return;
    }

    // Don't auto-check on auth callback page - let it handle the token first
    if (window.location.pathname === '/auth/callback') {
      setIsLoading(false);
      return;
    }

    checkAuthStatus();
  }, [isAuthEnabled]);

  const clearAuth = () => {
    setUser(null);
    setIsAuthenticated(false);
    setOrganization(null);
    // Clear stored token from localStorage
    localStorage.removeItem('auth_token');
    // Note: HTTP-only cookies cannot be cleared from JavaScript
    // They will be cleared by the backend logout endpoint
  };

  const signIn = () => {
    if (!isAuthEnabled) {
      return;
    }

    const currentPath = window.location.pathname;

    // Always include origin so backend knows which subdomain to route back to.
    // Exclude signin/callback pages — treat those as "go to home" on this subdomain.
    const returnPath = (currentPath === '/signin' || currentPath === '/auth/callback')
      ? '/'
      : currentPath;
    const returnUrl = window.location.origin + returnPath;

    const returnParam = `?return_url=${encodeURIComponent(returnUrl)}`;
    const authUrl = `/api/auth/google${returnParam}`;
    window.location.href = authUrl;
  };

  const signOut = async () => {
    try {
      // Always use relative path - Caddy proxy routes to backend
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include'
      });
      
      // Clear local state immediately
      clearAuth();
      
      // Force a page reload to reset the app and show login page
      // This ensures all state is cleared and auth check happens fresh
      window.location.href = '/';
    } catch (error) {
      // Logout error - still clear local state and redirect
      console.error('Logout error:', error);
      clearAuth();
      window.location.href = '/';
    }
  };

  const refreshAuth = useCallback(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  return {
    user,
    organization,
    isLoading,
    isAuthenticated,
    isAuthEnabled,
    signIn,
    signOut,
    checkAuthStatus,
    refreshAuth
  };
};